import { useState, useEffect, useMemo, useContext, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import GeolocationService, { GeoData } from '../Services/GeolocationService';
import RegistrationService from '../Services/RegistrationService';
import AnalyticsService, {
    AnalyticsEvents,
    GTM_ANALYTICS_EVENTS
} from '../Services/AnalyticsService';
import StorageService from '../Services/StorageService';
import { CLICK_START_TRIAL_ADDITIONAL_DATA_STORAGE_KEY } from '../constants/constants';
import UtilsService from '../Services/UtilsService';
import { TryForFreeCtaContext } from '../context/TryForFreeContext';
import ABTestToolServiceV2 from '../Services/ABTestToolService.v2';
import ApiService from '../Services/ApiService';
import { trackError } from '../utils/error.utils';
import { useNonUsaWarningModal } from './useNonUsaWarningModal';

interface UseRegistrationFormProps {
    source: string;
    couponCode?: string;
    dataForIntakeFormTest?: Record<string, any>;
    dataForIntentMarketingAi?: Record<string, any>;
    signupRedirectUrl?: string;
    handleSignupRedirectUrlCallback?: () => void;
    onBeforeSubmit?: () => Promise<void>;
    shouldShowButtonLoader?: boolean;
    openInNewTab?: boolean;
}

export const useRegistrationForm = ({
    source,
    couponCode,
    dataForIntakeFormTest,
    dataForIntentMarketingAi,
    signupRedirectUrl,
    handleSignupRedirectUrlCallback,
    onBeforeSubmit,
    shouldShowButtonLoader = false,
    openInNewTab = false
}: UseRegistrationFormProps) => {
    const { t } = useTranslation();

    const [isLoading, setIsLoading] = useState(false);
    const [geoLocationData, setGeoLocationData] = useState<
        GeoData | undefined
    >();

    const {
        isOpenNonUsaModal,
        isValidGeolocation,
        setIsOpenNonUsaModal,
        handleNonUsaModal,
        checkGeolocationAndExecute
    } = useNonUsaWarningModal();

    const utmParams = UtilsService.isBrowser() && RegistrationService.getUtms();

    const _couponCode = useMemo(() => {
        if (
            utmParams &&
            utmParams.utmCampaign?.toLowerCase() === 'beccaluna_msp'
        ) {
            return 'beccaluna';
        }
        return couponCode;
    }, [couponCode, utmParams]);

    const tryForFreeCtaContextValue = useContext(TryForFreeCtaContext);

    const buttonTextVariant = tryForFreeCtaContextValue?.isCtaTest
        ? t('registrationForm.tryForFree')
        : t('registrationForm.startFreeTrial');

    const getSignupLandingPage = useCallback(
        (locationPathname: string): string => {
            let signupLandingPage = locationPathname.split('?')[0];

            if (signupLandingPage[0] === '/') {
                signupLandingPage = signupLandingPage.substring(1);
            }

            if (signupLandingPage === '') {
                signupLandingPage = 'home';
            }

            return signupLandingPage;
        },
        []
    );

    const completeRegistrationForm = useCallback(() => {
        const data = RegistrationService.handleRegistration(geoLocationData);
        if (data && !data.promo && _couponCode) {
            data.promo = _couponCode;
        }

        if (dataForIntakeFormTest) {
            data.dataForIntakeFormTest = dataForIntakeFormTest;
        }
        if (signupRedirectUrl) {
            data.signupRedirectUrl = signupRedirectUrl;
        }

        RegistrationService.onRegistrationSuccessWithoutFields(
            data,
            openInNewTab
        );

        if (openInNewTab) {
            setIsLoading(false);
        }
    }, [
        geoLocationData,
        _couponCode,
        dataForIntakeFormTest,
        signupRedirectUrl,
        openInNewTab
    ]);

    useEffect(() => {
        if (!!signupRedirectUrl) {
            checkGeolocationAndExecute(() => {
                completeRegistrationForm();
            });
        }
    }, [
        signupRedirectUrl,
        checkGeolocationAndExecute,
        completeRegistrationForm
    ]);

    useEffect(() => {
        let isMounted = true;

        GeolocationService.getLocationPromise()
            .then(data => {
                if (isMounted) setGeoLocationData(data);
            })
            .catch(error => console.error(error));

        return () => {
            isMounted = false;
        };
    }, []);

    const handleGenerateIntentMarketingAi = useCallback(
        async (data?: Record<string, any>): Promise<void> => {
            if (data?.message) {
                try {
                    await ApiService.generateIntentMarketingAi(data.message);
                } catch (err) {
                    trackError(err, {
                        message: 'Intent generation failed',
                        dataForIntentMarketingAi
                    });
                }
            }
        },
        [dataForIntentMarketingAi]
    );

    const handleSubmit = useCallback(async () => {
        if (shouldShowButtonLoader) {
            return;
        }

        setIsLoading(true);

        handleSignupRedirectUrlCallback?.();

        AnalyticsService.trackGtm(GTM_ANALYTICS_EVENTS.cta_click, {
            click_text: buttonTextVariant,
            section: source
        });

        const additionalDataFromSessionStorage: Record<string, unknown> =
            StorageService.getItem(
                CLICK_START_TRIAL_ADDITIONAL_DATA_STORAGE_KEY,
                'sessionStorage'
            ) || {};

        const globalAdditionalDataPerPath =
            additionalDataFromSessionStorage[window.location.pathname] || {};

        const globalAdditionalData = Object.values(
            globalAdditionalDataPerPath
        ).reduce<Record<string, unknown>>(
            (data, current) => ({ ...data, ...current }),
            {}
        );

        const signupLandingPage = getSignupLandingPage(
            window.location.pathname
        );

        const abTestData =
            ABTestToolServiceV2?.getVariantPerTestInPage(
                window.location.pathname
            ) || {};

        await handleGenerateIntentMarketingAi(dataForIntentMarketingAi);

        AnalyticsService.trackClick(AnalyticsEvents.start_trial, {
            source,
            signup_landing_page: signupLandingPage,
            platform: 'gatsby',
            start_trial_variant: 'single-cta',
            user_choice: dataForIntentMarketingAi?.message,
            ...globalAdditionalData,
            ...abTestData
        });

        AnalyticsService.reportEvent('Intake Form Started');

        await onBeforeSubmit?.();

        // if handleSignupRedirectUrlCallback exist and signupRedirectUrl is empty we run completeRegistrationForm in useEffect after signupRedirectUrl is updated
        if (!!handleSignupRedirectUrlCallback && !signupRedirectUrl) {
            return;
        }

        checkGeolocationAndExecute(() => {
            completeRegistrationForm();
        });
    }, [
        shouldShowButtonLoader,
        handleSignupRedirectUrlCallback,
        buttonTextVariant,
        source,
        getSignupLandingPage,
        dataForIntentMarketingAi,
        handleGenerateIntentMarketingAi,
        onBeforeSubmit,
        handleSignupRedirectUrlCallback,
        signupRedirectUrl,
        checkGeolocationAndExecute,
        completeRegistrationForm
    ]);

    return {
        isLoading,
        isValidGeolocation,
        isOpenNonUsaModal,
        buttonTextVariant,
        handleSubmit,
        completeRegistrationForm,
        handleNonUsaModal,
        setIsOpenNonUsaModal,
        checkGeolocationAndExecute
    };
};
